<template>
  <!--
    根节点始终存在，便于 mounted 时整棵 DOM 挂到 document.body。
    内容区按 calls 显隐：有未处理呼叫才展示弹窗。
  -->
  <div class="therapist-call-portal" aria-live="assertive">
    <transition-group
      v-if="calls.length"
      name="call-banner"
      tag="div"
      class="therapist-call-stack therapist-call-anchor"
    >
      <div
        v-for="item in calls"
        :key="item.therapist_call_id"
        class="therapist-call-popup"
      >
        <span class="call-popup-icon" aria-hidden="true">!</span>
        <!--
          大屏告警仅两行：
          1）呼叫房间 room_name
          2）该房间当前治疗师 scheduled_staff_name（预约/在岗治疗师）
          不展示 content、caller_staff_name，避免「谁发起呼叫」等冗余文案。
        -->
        <div class="call-popup-body">
          <div class="call-popup-room">{{ item.room_name || '—' }}</div>
          <div class="call-popup-therapist">
            {{ item.scheduled_staff_name || '—' }}
          </div>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
/**
 * 文件简介：
 * - 文件名：TherapistCallBanner.vue
 * - 作用：元气大屏「房间请求协助」居中告警浮层
 * - 使用方法：由 sleep.vue 传入 therapistCalls；有数据即展示，前台处理后清空
 * - 展示约定（两行）：
 *   1. 第一行：呼叫房间（room_name）
 *   2. 第二行：当前房间治疗师（scheduled_staff_name）
 *   不渲染 content / caller_staff_name（发起人），即便 WS 载荷里有这些字段
 * - 原理：
 *   1. Vue2 无 Teleport，mounted 时把根节点挂到 document.body，
 *      避免大屏 .modules-wrapper 轮播 transform / overflow 影响 position:fixed
 *   2. 与患者页轮播解耦：告警不随切页滑走，直到收到 YUYUE_CALL 移除
 */
export default {
  name: 'TherapistCallBanner',
  props: {
    /**
     * 未处理呼叫列表。
     * 展示用：room_name、scheduled_staff_name、therapist_call_id（key）。
     * 不展示：content、caller_staff_name（仍可能存在于对象中，供日志等使用）。
     */
    calls: {
      type: Array,
      default: () => []
    }
  },
  mounted() {
    // 挂到 body：与大屏轮播 DOM 树解耦，保证 fixed 相对视口且不被 overflow 裁切
    if (typeof document !== 'undefined' && this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el);
    }
  },
  beforeDestroy() {
    // 组件销毁时从 body 移除，避免残留空节点
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  }
}
</script>

<style scoped>
/* 门户根节点：无尺寸、不拦截点击，仅作为挂载容器 */
.therapist-call-portal {
  pointer-events: none;
}

/* 居中浮层：不遮罩整屏，不阻挡大屏内容浏览 */
.therapist-call-anchor {
  position: fixed;
  left: 50%;
  top: 50%;
  z-index: 99999;
  transform: translate(-50%, -50%);
  pointer-events: none;
  max-width: min(400px, 88vw);
  max-height: 70vh;
}

.therapist-call-stack {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  max-height: 70vh;
  overflow-y: auto;
}

.therapist-call-popup {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  color: #fff;
  text-align: left;
  box-shadow:
    0 8px 28px rgba(0, 18, 46, 0.28),
    0 0 0 1px rgba(255, 183, 77, 0.4);
  background: linear-gradient(
    135deg,
    rgba(255, 167, 38, 0.96) 0%,
    rgba(245, 124, 0, 0.94) 100%
  );
  border: 1px solid rgba(255, 224, 178, 0.65);
  animation: call-popup-attention 2s ease-in-out infinite;
}

@keyframes call-popup-attention {
  0%, 100% { box-shadow: 0 8px 28px rgba(0, 18, 46, 0.28), 0 0 0 1px rgba(255, 183, 77, 0.4); }
  50% { box-shadow: 0 10px 32px rgba(230, 81, 0, 0.35), 0 0 0 2px rgba(255, 224, 178, 0.55); }
}

.call-popup-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
  color: #e65100;
  background: rgba(255, 255, 255, 0.95);
}

.call-popup-body {
  flex: 1;
  min-width: 0;
}

/* 第一行：呼叫房间名，主信息、字号加大便于远距离辨认 */
.call-popup-room {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 4px;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 18, 46, 0.2);
}

/* 第二行：当前房间治疗师（scheduled_staff_name） */
.call-popup-therapist {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  opacity: 0.95;
  color: rgba(255, 255, 255, 0.95);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.call-banner-enter-active,
.call-banner-leave-active {
  transition: all 0.35s ease;
}

.call-banner-enter,
.call-banner-leave-to {
  opacity: 0;
  transform: scale(0.92) translateY(8px);
}

.call-banner-move {
  transition: transform 0.35s ease;
}
</style>
